import { trapFocus, debounce, fetchConfig, createSlider, THEME_ERROR_ICON, THEME_SUCCESS_ICON } from '@theme/theme-global';
import { listen, dispatch } from '@theme/events';
import { SKY_INPUT_DEBOUNCE_MS, SKY_THEME_EVENTS } from '@theme/config';

class BagRemoveLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('bag-lines') || this.closest('bag-drawer-lines');
      cartItems.changeLineQty(this.dataset.index, 0, event);
    });
  }
}

customElements.define('bag-remove-link', BagRemoveLink, {
  extends: 'a',
});

export class BagLines extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    const debouncedOnChange = debounce((event) => {
      this.onLineChange(event);
    }, SKY_INPUT_DEBOUNCE_MS);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = listen(SKY_THEME_EVENTS.cartUpdate, (event) => {
      if (event.source === 'bag-lines') {
        return;
      }
      return this.refreshFromCart();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  restoreQtyInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  showQtyValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.restoreQtyInput(index);
    event.target.select();
  }

  checkQtyRules(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.cartQuantityStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.cartQuantityStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.cartQuantityStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      if (!event.target.matches('input[name="updates[]"]')) {
        return;
      }
      this.showQtyValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.changeLineQty(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onLineChange(event) {
    this.checkQtyRules(event);
  }

  refreshFromCart() {
    if (this.tagName === 'BAG-DRAWER-LINES') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = [
            'bag-drawer-lines',
            '[bag-drawer-footer]',
            '[data-cart-saved-line="cart-drawer"]',
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('bag-lines');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  sectionsToRefresh() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '[data-main-cart-items]',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '[data-cart-saved-line="cart"]',
      },
      {
        id: 'free-shipping-box',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '[data-shipping-box]',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '[data-main-cart-footer-inner]',
      },
      {
        id: 'cart-count',
        section: 'cart-count',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }

  changeLineQty(line, quantity, event, name, variantId) {
    this.startLineLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionsToRefresh().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.announceLineMessage(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('bag-drawer');
        // const cartFooter = document.getElementById('main-cart-footer');
        // if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.sectionsToRefresh().forEach((section) => {
          const sectionRoot = document.getElementById(section.id);
          if (!sectionRoot) return;

          const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
          if (section.id === 'free-shipping-box') {
            const sectionHTML = parsedState.sections[section.section];
            if (sectionHTML) {
              const shippingBarNew = new DOMParser()
                .parseFromString(sectionHTML, 'text/html')
                .querySelector(section.selector);
              if (shippingBarNew) {
                const shippingMessage = shippingBarNew.querySelector('[data-shipping-message]');
                const oldShippingMessage = document.querySelector('[data-shipping-message]');
                if (shippingMessage && oldShippingMessage) {
                  oldShippingMessage.innerHTML = shippingMessage.innerHTML;
                }
                const progressBar = shippingBarNew.querySelector('[data-shipping-bar-fill]');
                const oldProgressBar = document.querySelector('[data-shipping-bar-fill]');
                if (progressBar && oldProgressBar) {
                  oldProgressBar.style.setProperty('--shippingProgress', progressBar.dataset.barWidth);
                  oldProgressBar.dataset.barWidth = progressBar.dataset.barWidth;
                }
              }
            }
            return;
          }

          if (!parsedState.sections?.[section.section]) return;
          const sectionHtml = this.extractSectionHtml(parsedState.sections[section.section], section.selector);
          if (sectionHtml === null) return;
          elementToReplace.innerHTML = sectionHtml;
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.announceLineMessage(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        dispatch(SKY_THEME_EVENTS.cartUpdate, {
          source: 'bag-lines',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading-spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (!errors) return;
        const textEl = errors.querySelector('[data-cart-errors-text]');
        if (textEl) {
          textEl.textContent = window.cartStrings.error;
        } else {
          errors.textContent = window.cartStrings.error;
        }
        errors.hidden = false;
        errors.classList.remove('hidden');
      })
      .finally(() => {
        this.stopLineLoading(line);
      });
  }

  announceLineMessage(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('[data-cart-item-error-text]').textContent = message;
      lineItemError.classList.toggle('hidden', !message);
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  extractSectionHtml(html, selector) {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  startLineLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart-items-disabled');
    }

    [
      ...this.querySelectorAll(`#CartItem-${line} .loading-spinner`),
      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-spinner`),
    ].forEach((spinner) => spinner.classList.remove('hidden'));

    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    // Optionally unhide status element if needed
    // this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  stopLineLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart-items-disabled');
    }

    [
      ...this.querySelectorAll(`#CartItem-${line} .loading-spinner`),
      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-spinner`),
    ].forEach((spinner) => spinner.classList.add('hidden'));
  }
}

customElements.define('bag-lines', BagLines);

if (!customElements.get('bag-note')) {
  customElements.define(
    'bag-note',
    class BagNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, SKY_INPUT_DEBOUNCE_MS),
        );
      }
    },
  );
}

class BagDiscount extends HTMLFormElement {
  constructor() {
    super();
    this.addEventListener('submit', this.handleFormSubmit.bind(this));
    this.source = this.dataset.source;
  }

  connectedCallback() {
    if (this.source == 'cart') {
      this.mainCart = document.querySelector('bag-lines');
    } else {
      this.cart = document.querySelector('bag-drawer');
    }
  }
  get cartDiscountsEl() {
    return this.querySelector('.applied-cart-discounts');
  }
  get couponEl() {
    return (this._couponEl ||= this.querySelector('input[name="discount"]'));
  }

  get messageEl() {
    return (this._messageEl ||= this.querySelector('[form-error]'));
  }
  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  async handleFormSubmit(event) {
    event.preventDefault();
    this.displayMessage();

    const discountCode = this.couponEl?.value?.trim();
    if (!discountCode) {
      this.displayMessage('error', window.cartStrings.discountCodeRequired);
      return;
    }

    const discounts = this.getDiscounts();
    if (discounts.includes(discountCode)) {
      this.displayMessage('error', window.cartStrings.discountCodeAlreadyApplied);
      return;
    }

    discounts.push(discountCode);

    try {
      const response = await this.applyDiscount(discounts);

      if (response.status) {
        this.displayMessage('error', response.errors?.join(', ') || window.cartStrings.discountCodeError);
        return;
      }

      const invalid = response.discount_codes.find((d) => d.code === discountCode && !d.applicable);
      if (invalid) {
        this.couponEl.value = '';
        this.displayMessage('error', window.cartStrings.discountCodeNotApplicable);
        return;
      }

      if (this.cart) {
        this.cart.applySectionHtml(response);
      }
      if (this.mainCart) {
        this.refreshBagPage(response);
      }
    } catch (error) {
      console.error('Error updating discount:', error);
      this.displayMessage('error', window.cartStrings.discountCodeApplyError);
    }
  }

  async applyDiscount(discounts) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();

    formData.append('discount', discounts.join(','));
    if (this.cart) {
      formData.append(
        'sections',
        this.cart.sectionsToRefresh().map((s) => s.id),
      );
      formData.append('sections_url', window.location.pathname);
    }
    if (this.mainCart) {
      formData.append(
        'sections',
        this.mainCart.sectionsToRefresh().map((section) => section.section),
      );
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;
    const res = await fetch(routes.cart_update_url, config);
    return res.json();
  }

  refreshBagPage(parsedState) {
    this.mainCart.sectionsToRefresh().forEach((section) => {
      const sectionRoot = document.getElementById(section.id);
      if (!sectionRoot || !parsedState.sections?.[section.section]) return;
      const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
      const sectionHtml = this.mainCart.extractSectionHtml(parsedState.sections[section.section], section.selector);
      if (sectionHtml === null) return;
      elementToReplace.innerHTML = sectionHtml;
    });
  }
  displayMessage(type, message) {
    if (!this.messageEl) {
      if (message) alert(message);
      return;
    }
    if (message) {
      this.messageEl.classList.remove('hidden');
      const textEl = this.messageEl.querySelector('[form-error-text]');
      if (textEl) {
        textEl.textContent = message;
      } else {
        this.messageEl.textContent = message;
      }
      this.messageEl.dataset.type = type;
    } else {
      this.messageEl.classList.add('hidden');
      const textEl = this.messageEl.querySelector('[form-error-text]');
      if (textEl) textEl.textContent = '';
    }
  }
}

customElements.define('bag-discount', BagDiscount, { extends: 'form' });

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  get errorIcon() {
    return THEME_ERROR_ICON();
  }

  get successIcon() {
    return THEME_SUCCESS_ICON();
  }

  formatError(errors) {
    const errorItems = Object.keys(errors)
      .map(
        (errorKey) => `
        <li>
          <span class="d-flex align-items-center gap-1 form-message-small form-message-error">
            ${this.errorIcon}
            ${errors[errorKey]}
          </span>
        </li>`,
      )
      .join('');

    this.resultsElement.innerHTML = `
      <div class="d-flex flex-column gap-2 mt-10">
        <p class="d-flex align-items-center gap-1 form-message-small form-message-error font-bold" role="alert" tabindex="-1">
          ${this.errorIcon}
          ${window.cartStrings.shippingCalculatorError}
        </p>
        <ul class="d-flex flex-column gap-1" role="list">${errorItems}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    if (shippingRates.length === 0) {
      this.resultsElement.innerHTML = `
        <p class="d-flex align-items-center gap-1 form-message-small form-message-error mt-10" role="alert" tabindex="-1">
          ${this.errorIcon}
          ${window.cartStrings.shippingCalculatorNoRates}
        </p>
      `;
      return;
    }

    const heading =
      shippingRates.length === 1
        ? window.cartStrings.shippingCalculatorOneResult
        : window.cartStrings.shippingCalculatorMultipleResults;

    const rateItems = shippingRates
      .map(
        ({ presentment_name, currency, price }) => `
        <li>
          <span class="d-flex align-items-center gap-1 form-message-small form-message-success">
            ${this.successIcon}
            ${presentment_name}: ${currency} ${price}
          </span>
        </li>`,
      )
      .join('');

    this.resultsElement.innerHTML = `
      <div class="d-flex flex-column gap-2 mt-10">
        <p class="d-flex align-items-center gap-1 form-message-small form-message-success font-bold" role="status" tabindex="-1">
          ${this.successIcon}
          ${heading}
        </p>
        <ul class="d-flex flex-column gap-1" role="list">${rateItems}</ul>
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, {
  extends: 'form',
});

class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');

    // Extract options from template if present
    const template = this.querySelector('template');
    if (template && this.countryElement) {
      const templateContent = document.importNode(template.content, true);
      // Find option elements in the template and append to countryElement
      const optionNodes = templateContent.querySelectorAll('option');
      optionNodes.forEach((option) => {
        this.countryElement.appendChild(option.cloneNode(true));
      });
    }

    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

    this.init();
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country),
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class DiscountRemoveButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  async handleClick(event) {
    event.preventDefault();

    const discountCode = this.dataset.discountCode;
    if (!discountCode) return;

    const container = this.closest('#main-cart-items') || this.closest('bag-drawer') || document;
    const discounts = [...container.querySelectorAll('[is="discount-remove-btn"][data-discount-code]')]
      .map((btn) => btn.dataset.discountCode)
      .filter((code) => code && code !== discountCode);

    const mainCart = document.querySelector('bag-lines');
    const cartDrawer = document.querySelector('bag-drawer');

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('discount', discounts.join(','));

      if (this.closest('#main-cart-items') && mainCart) {
        formData.append(
          'sections',
          mainCart.sectionsToRefresh().map((section) => section.section),
        );
      } else if (cartDrawer) {
        formData.append(
          'sections',
          cartDrawer.sectionsToRefresh().map((section) => section.id),
        );
      }
      formData.append('sections_url', window.location.pathname);

      config.body = formData;
      const response = await fetch(routes.cart_update_url, config).then((res) => res.json());

      if (response.status) {
        console.error('Error removing discount:', response.errors);
        return;
      }

      if (this.closest('#main-cart-items') && mainCart) {
        mainCart.sectionsToRefresh().forEach((section) => {
          const sectionRoot = document.getElementById(section.id);
          if (!sectionRoot || !response.sections?.[section.section]) return;
          const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
          const sectionHtml = mainCart.extractSectionHtml(response.sections[section.section], section.selector);
          if (sectionHtml === null) return;
          elementToReplace.innerHTML = sectionHtml;
        });
      } else if (cartDrawer) {
        cartDrawer.applySectionHtml(response);
      }

      dispatch(SKY_THEME_EVENTS.cartUpdate, {
        source: 'bag-lines',
        cartData: response,
      });
    } catch (error) {
      console.error('Error removing discount:', error);
    }
  }
}
customElements.define('discount-remove-btn', DiscountRemoveButton, { extends: 'button' });


class CartDealsProducts extends HTMLDivElement {
  constructor() {
    super();
    this.slider = null;
    this._sliderInitializing = false;
    this._onTabChange = this._onTabChange.bind(this);
  }

  connectedCallback() {
    this.tabComponent = this.closest('tab-component');
    this.tabComponent?.addEventListener('tabChange', this._onTabChange);
    this.initSlider();
  }

  disconnectedCallback() {
    this.tabComponent?.removeEventListener('tabChange', this._onTabChange);
    this.tabComponent = null;

    if (this.slider) {
      this.slider.destroy(true, true);
      this.slider = null;
    }
  }

  _onTabChange(event) {
    if (!event.detail?.panel?.contains(this)) return;
    if (!this.slider || this.slider.destroyed) return;

    requestAnimationFrame(() => {
      this.slider?.update?.();
    });
  }

  revealPanelForMeasure(panel) {
    if (!panel?.hidden) return null;

    panel.hidden = false;
    panel.style.visibility = 'hidden';
    panel.style.pointerEvents = 'none';

    return () => {
      panel.hidden = true;
      panel.style.visibility = '';
      panel.style.pointerEvents = '';
    };
  }

  async initSlider() {
    if (this.slider || this._sliderInitializing || !this.dataset.params) return;

    this._sliderInitializing = true;
    const panel = this.closest('[data-tab-content]');
    const restorePanel = this.revealPanelForMeasure(panel);

    try {
      if (restorePanel) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      this.slider = await createSlider(this, JSON.parse(this.dataset.params));
    } finally {
      restorePanel?.();
      this._sliderInitializing = false;
    }
  }
}
customElements.define('cart-deals-products', CartDealsProducts, { extends: 'div' });